/**
 * SchemaIssueV1 — the ONE shape of a closed-schema defect in a model reply,
 * and its ONE renderer.
 *
 * A validator records WHERE (the JSON path), WHAT (the constraint) and, for a
 * bounded value, the measured ACTUAL and its LIMIT. The repair turn, the
 * failure record and the log line all render the same issue the same way, so
 * no consumer re-parses validator prose to recover the path or the limit (the
 * compose plan's hand-kept allowlist did, and every message it had not copied
 * reached the model as "Strict plan schema/admission failed", twice in a row
 * on tranquility-nine).
 *
 * An issue carries no free text of the reply: `actual` is a number, `limit`
 * and `constraint` are the validator's own words, and path segments that come
 * from the reply's own keys go through {@link schemaKeySegment}.
 */
export interface SchemaIssueV1 {
  /** JSON path from the reply's root: `$` (the reply itself), `missing[0].sketch`, `art.slots["pad"]`. */
  path: string;
  /** What must hold, in the validator's words: "too long", "one of template, adapt", "unknown field". */
  constraint: string;
  /** The measured quantity (a length, a count) when the constraint bounds one. */
  actual?: number;
  /** The bound, a number or a range ("1..600"). */
  limit?: number | string;
  /** Unit of `actual` and a numeric `limit` ("chars", "items"). */
  unit?: string;
}

/** `missing[0].sketch: too long (actual 885 chars, limit 600 chars)`. */
export function schemaIssueText(issue: SchemaIssueV1): string {
  const unit = issue.unit ? ` ${issue.unit}` : '';
  const measures = [
    ...(issue.actual !== undefined ? [`actual ${issue.actual}${unit}`] : []),
    ...(issue.limit !== undefined ? [`limit ${issue.limit}${unit}`] : []),
  ];
  return `${issue.path}: ${issue.constraint}${measures.length ? ` (${measures.join(', ')})` : ''}`;
}

/** A thrown set of issues: the message renders every one, `issues` keeps them typed. */
export class SchemaIssuesError extends Error {
  readonly issues: readonly SchemaIssueV1[];
  constructor(issues: readonly SchemaIssueV1[]) {
    super(issues.map(schemaIssueText).join('; '));
    this.name = 'SchemaIssuesError';
    this.issues = issues;
  }
}

/** Throw when there is at least one issue. */
export function throwSchemaIssues(issues: readonly SchemaIssueV1[]): void {
  if (issues.length) throw new SchemaIssuesError(issues);
}

/** The issues a thrown error carries, or null when it is not a schema failure. */
export function schemaIssuesOf(error: unknown): readonly SchemaIssueV1[] | null {
  return error instanceof SchemaIssuesError ? error.issues : null;
}

/** `issue` re-rooted under `base` (a reply file such as `integration.json`). */
export function schemaIssueUnder(base: string, issue: SchemaIssueV1): SchemaIssueV1 {
  if (base === '$') return issue;
  const path = issue.path === '$' ? base : issue.path.startsWith('$') ? `${base}${issue.path.slice(1)}` : `${base}.${issue.path}`;
  return { ...issue, path };
}

/** The rendered issues a thrown schema failure carries, under `base`. Any
 * other error is a platform fault, not repair feedback, and is rethrown. */
export function schemaIssueLinesOf(error: unknown, base = '$'): string[] {
  const issues = schemaIssuesOf(error);
  if (!issues) throw error;
  return issues.map(issue => schemaIssueText(schemaIssueUnder(base, issue)));
}

const SAFE_KEY =/^[A-Za-z_][A-Za-z0-9_-]{0,47}$/;

/** A path segment for a key the REPLY chose (an unknown field, a slot name):
 * `.key` or `["key-name"]` when it is identifier-like, else a positional
 * placeholder, so reply prose never rides into a path. */
export function schemaKeySegment(key: string, index: number): string {
  if (/^[A-Za-z_][A-Za-z0-9_]{0,47}$/.test(key)) return `.${key}`;
  return SAFE_KEY.test(key) ? `["${key}"]` : `[<key ${index + 1}>]`;
}

/** `base` + a reply-chosen key, `$` meaning the root. */
export function schemaKeyPath(base: string, key: string, index: number): string {
  const segment = schemaKeySegment(key, index);
  return base === '$' ? (segment.startsWith('.') ? segment.slice(1) : `$${segment}`) : `${base}${segment}`;
}

/** Every key of `value` outside `allowed`, one issue each. */
export function unknownFieldIssues(value: Record<string, unknown>, allowed: readonly string[], path: string): SchemaIssueV1[] {
  return Object.keys(value).flatMap((key, index) => allowed.includes(key) ? []
    : [{ path: schemaKeyPath(path, key, index), constraint: `unknown field; allowed fields: ${allowed.join(', ')}` }]);
}

/** A trimmed text's length against `min..max` chars, or null when it fits. */
export function textLengthIssue(path: string, value: unknown, max: number, min = 1): SchemaIssueV1 | null {
  if (typeof value !== 'string') return { path, constraint: `must be a string of ${min}..${max} chars` };
  const length = value.trim().length;
  if (length >= min && length <= max) return null;
  return length < min
    ? { path, constraint: length === 0 ? 'empty' : 'too short', actual: length, limit: `${min}..${max}`, unit: 'chars' }
    : { path, constraint: 'too long', actual: length, limit: max, unit: 'chars' };
}

/** An array's item count against `max`, or the type when it is not an array. */
export function arrayCountIssue(path: string, value: unknown, max: number, min = 0): SchemaIssueV1 | null {
  if (!Array.isArray(value)) return { path, constraint: `must be an array of ${min}..${max} items` };
  if (value.length >= min && value.length <= max) return null;
  return value.length > max
    ? { path, constraint: 'too many items', actual: value.length, limit: max, unit: 'items' }
    : { path, constraint: 'too few items', actual: value.length, limit: `${min}..${max}`, unit: 'items' };
}

const PATH = String.raw`(?:\$|[A-Za-z_][A-Za-z0-9_-]*)(?:\.[A-Za-z_][A-Za-z0-9_-]*|\[\d+\]|\["[A-Za-z_][A-Za-z0-9_-]*"\]|\[<key \d+>\])*`;
/** Matches one rendered issue: a JSON path (or a comma list of the exact paths
 * one defect spans, such as two routes of one signal), ": ", the constraint. */
export const SCHEMA_ISSUE_TEXT = new RegExp(`^${PATH}(?:, ${PATH})*: \\S`);
